import { Link } from "react-router-dom";
import { AlertTriangle, AlertOctagon } from "lucide-react";
import { useMeStatus } from "../hooks/useGuardianStatus";

/**
 * NoticeBlock — sticky top banner rendered for any authenticated user
 * whose `account_status` is `warning` or `frozen`. Copy is written by
 * the backend (`/api/me/status.notice`) so admins can adjust it once
 * without a redeploy.
 *
 * A `blocked` user is redirected to `/blocked` in App.js — this
 * component never renders for them.
 */
export default function NoticeBlock() {
  const { data } = useMeStatus();
  const status = data?.account_status;

  if (!status || status === "active" || status === "blocked") return null;

  // Deep-link the contact page with context so support reps see the
  // status, user_id and referrer in the ticket without asking.
  const contactHref = `/contact?ref=account_status&status=${encodeURIComponent(status)}`
    + (data?.user_id ? `&user=${encodeURIComponent(data.user_id)}` : "");

  const Icon = status === "frozen" ? AlertOctagon : AlertTriangle;
  const tone = status === "frozen"
    ? "bg-red-950/95 border-red-500/50 text-red-100"
    : "bg-amber-950/95 border-amber-500/50 text-amber-100";

  // Parse **bold** segments in the copy — mimics markdown emphasis.
  const notice = data?.notice || "";
  const parts = notice.split(/(\*\*[^*]+\*\*)/g).map((seg, i) =>
    seg.startsWith("**") && seg.endsWith("**")
      ? <strong key={i}>{seg.slice(2, -2)}</strong>
      : <span key={i}>{seg}</span>
  );

  return (
    <div
      role="alert"
      data-testid={`notice-block-${status}`}
      className={`sticky top-0 z-[80] border-b ${tone} backdrop-blur-md`}
    >
      <div className="max-w-6xl mx-auto px-4 py-2 flex items-center gap-3 text-sm">
        <Icon className="w-4 h-4 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          {parts}{" "}
          <Link
            to={contactHref}
            data-testid="notice-block-contact-link"
            className="underline underline-offset-2 hover:opacity-80 font-semibold"
          >
            Click Here…
          </Link>
        </div>
      </div>
    </div>
  );
}
